// Angular
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
// RxJs
import { Observable, of } from 'rxjs';
// Lodash
import { includes, noop, toString } from 'lodash';
// Sentry
import * as Sentry from '@sentry/angular';
// Environment
import { environment } from '@environment';
// Store
import { Store } from '@ngrx/store';
import { Logout } from '@app/core/auth';

@Injectable({
    providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
    readonly injector = inject(Injector);

    /**
     * Handle failed operation and keep app alive
     */
    public handleError<T>(operation?: any, error?: any, isApiError: boolean = false): Observable<any> {
        if (!error && !operation) {
            return of([]);
        }

        // Handle JWT Expiry or Authorization
        if (error instanceof HttpErrorResponse && error.status === 401) {
            const store = this.injector.get(Store);
            alert('Your session has expired. Please relogin');
            const returnUrl = window.location?.pathname !== '/'
                ? window.location.pathname + window.location.search
                : undefined;
            store.dispatch(new Logout({ returnUrl }));
            return of(false);
        }

        // Skip HTML-as-JSON parsing errors
        if (isApiError && this.isHtmlApiResponse(error)) {
            return of(error);
        }

        // Skip status: 0, undefined
        if (error instanceof HttpErrorResponse && error.status === 0) {
            return of(error);
        }

        // Skip dynamic chunk load errors
        if (!isApiError && this.isChunkLoadError(error)) {
            return of(error);
        }

        // Skip aborted requests
        if (this.isAbortError(error)) {
            return of(error);
        }

        // Construct Error Msg for Sentry and Console Logging
        const errorMsg: string = this.constructErrorMsg(operation, error, isApiError);

        if (environment.isSentryLoggingEnable) {
            // Skip API 400, 403, 404, 406, 410, 429, 451 Errors
            const skipLoggingStatusCodes: Array<number> = [400, 403, 404, 406, 410, 429, 451];

            if (isApiError && includes(skipLoggingStatusCodes, error?.status)) {
                noop();
            } else if (error instanceof Error) {
                Sentry.captureException(error);
            } else {
                Sentry.captureMessage(errorMsg);
            }
        } else {
            console.error(errorMsg);
        }

        // Let the app keep running by returning an empty result.
        return of(error);
    }

    /**
     * Aborted requests
     */
    private isAbortError(error: any): boolean {
        const msg = error?.message || error?.toString?.() || '';

        return (
            error?.name === 'AbortError' ||
            msg.includes('AbortError') ||
            msg.includes('aborted a request')
        );
    }

    /**
     * Dynamic chunk load errors
     */
    private isChunkLoadError(error: any): boolean {
        const msg = error?.message || error?.toString?.() || '';

        return (
            msg.includes('error loading dynamically imported module') ||
            msg.includes('Failed to fetch dynamically imported module') ||
            msg.includes('Loading chunk') ||
            msg.includes('ChunkLoadError') ||
            msg.includes('text/html') ||
            msg.includes('valid JavaScript MIME type')
        );
    }

    /**
     * Detect HTML instead of JSON
     */
    private isHtmlApiResponse(error: any): boolean {
        return (
            error instanceof HttpErrorResponse &&
            typeof error.message === 'string' &&
            error.message.includes('Http failure during parsing')
        );
    }

    /**
     * Create custom string error message
     */
    private constructErrorMsg(operation: any, error: any, isApiError: boolean): string {
        const startMsg: string = isApiError ? 'API ERROR' : 'JS ERROR';
        let errorMsg = `${startMsg} | ${operation}`;

        if (typeof error === 'string') {
            errorMsg += ` | ${error}`;
        }

        if (error?.message) {
            errorMsg += ` | ${error.message}`;
        }

        if (error?.stack) {
            errorMsg += ` | ${toString(error.stack)}`;
        }

        if (error instanceof HttpErrorResponse) {
            errorMsg += ` | ${error?.url} | ${error?.message} | ${error?.error?.message} | ${error?.error?.details}`;
        }

        if (error instanceof ErrorEvent) {
            errorMsg += ` | ${error?.error?.message}`;
        }

        // Override message in case of application error
        if (operation && operation.type === 'error') {
            errorMsg = `JS | APPLICATION ERROR | ${operation?.message ?? operation?.type ?? 'Unknown application error'}`;
        }

        return errorMsg;
    }
}